import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BackendService,
  HttpOptions,
  PaginatedResponseModel,
} from '@ipg/ngx-frontend-toolset';
import {
  AiAgentSessionCreateRequest,
  AiAgentSessionSendMessageRequest,
  AiAgentSessionUpdateRequest,
  BackendAiAgentSession,
} from '../components/ai-agent-chat/ai-agent-chat.models';

@Injectable({
  providedIn: 'root',
})
export class AiAgentSessionService {
  private readonly _backend = inject(BackendService);

  list(
    params?: {
      clientId?: string | null;
      agentName?: string;
      status?: string;
      page?: number;
      limit?: number;
    },
    options?: HttpOptions
  ): Observable<PaginatedResponseModel<BackendAiAgentSession[]>> {
    const queryParams: Record<string, string | number> = {};
    if (params?.clientId) {
      queryParams['client_id'] = params.clientId;
    }
    if (params?.agentName) {
      queryParams['agent_name'] = params.agentName;
    }
    if (params?.status) {
      queryParams['status'] = params.status;
    }
    if (params?.page !== undefined) {
      queryParams['page'] = params.page;
    }
    if (params?.limit !== undefined) {
      queryParams['limit'] = params.limit;
    }

    return this._backend.get<PaginatedResponseModel<BackendAiAgentSession[]>>(
      '/ai-agent-sessions',
      {
        ...options,
        params: {
          ...queryParams,
          ...options?.params,
        },
      }
    );
  }

  getById(
    id: string,
    options?: HttpOptions
  ): Observable<BackendAiAgentSession> {
    return this._backend.get<BackendAiAgentSession>(
      `/ai-agent-sessions/${id}`,
      options
    );
  }

  create(
    data: AiAgentSessionCreateRequest,
    options?: HttpOptions
  ): Observable<BackendAiAgentSession> {
    return this._backend.post<BackendAiAgentSession>(
      '/ai-agent-sessions',
      {
        agent_name: data.agentName ?? 'interaction_creator',
        title: data.title ?? 'Nieuwe interactie',
        client_id: data.clientId ?? null,
        initial_message: data.initialMessage ?? null,
      },
      options
    );
  }

  sendMessage(
    id: string,
    data: AiAgentSessionSendMessageRequest,
    options?: HttpOptions
  ): Observable<BackendAiAgentSession> {
    return this._backend.post<BackendAiAgentSession>(
      `/ai-agent-sessions/${id}/messages`,
      {
        message: data.message,
      },
      options
    );
  }

  /**
   * Re-runs the turn a failure chip is offering to retry. Sends no message:
   * the backend re-opens the session so the agent replays the exact transcript
   * it had before the failure.
   */
  retry(id: string, options?: HttpOptions): Observable<BackendAiAgentSession> {
    return this._backend.post<BackendAiAgentSession>(
      `/ai-agent-sessions/${id}/retry`,
      {},
      options
    );
  }

  update(
    id: string,
    data: AiAgentSessionUpdateRequest,
    options?: HttpOptions
  ): Observable<BackendAiAgentSession> {
    return this._backend.patch<BackendAiAgentSession>(
      `/ai-agent-sessions/${id}`,
      {
        title: data.title,
        status: data.status,
      },
      options
    );
  }

  createInteraction(
    sessionId: string,
    interactionData: unknown,
    messageId?: string | null,
    options?: HttpOptions
  ): Observable<BackendAiAgentSession> {
    const payload: Record<string, unknown> = {
      interaction_data: interactionData,
    };
    if (messageId) {
      payload['message_id'] = messageId;
    }
    return this._backend.post<BackendAiAgentSession>(
      `/ai-agent-sessions/${sessionId}/create-interaction`,
      payload,
      options
    );
  }

  delete(
    id: string,
    options?: HttpOptions
  ): Observable<{ success: boolean }> {
    return this._backend.delete<{ success: boolean }>(
      `/ai-agent-sessions/${id}`,
      options
    );
  }
}
